import {
  Alert,
  Box,
  Button,
  FormControl,
  FormControlLabel,
  Paper,
  Radio,
  RadioGroup,
  Skeleton,
  Stack,
  Typography,
} from "@mui/material";
import { useEffect } from "react";
import type { StripePaymentMethod } from "../api/private/getStripePaymentMethods";
import { getBrandImageSrc } from "./creditCard/brandAssets";

interface SavedPaymentMethodSelectorProps {
  paymentMethods: StripePaymentMethod[];
  isLoading: boolean;
  error: Error | null;
  selectedPaymentMethod: string | null;
  onSelectionChange: (value: string | null) => void;
  defaultPaymentMethodId?: string;
  hideAddNewButton?: boolean;
  refetchPaymentMethods: () => void;
  includeNewCardRadio?: boolean;
}

export default function SavedPaymentMethodSelector({
  paymentMethods,
  isLoading,
  error,
  selectedPaymentMethod,
  onSelectionChange,
  defaultPaymentMethodId,
  hideAddNewButton = false,
  refetchPaymentMethods: _refetchPaymentMethods,
  includeNewCardRadio = false,
}: SavedPaymentMethodSelectorProps) {
  // Auto-select default payment method when available and no selection made yet
  useEffect(() => {
    if (
      defaultPaymentMethodId &&
      paymentMethods.length > 0 &&
      !selectedPaymentMethod &&
      !isLoading
    ) {
      // Verify the default payment method exists in the available methods
      const defaultExists = paymentMethods.some((method) => method.id === defaultPaymentMethodId);
      if (defaultExists) {
        onSelectionChange(defaultPaymentMethodId);
      }
    }
  }, [defaultPaymentMethodId, paymentMethods, selectedPaymentMethod, isLoading, onSelectionChange]);
  if (isLoading) {
    return (
      <Box sx={{ mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Choose Your Payment Method
        </Typography>
        <Stack spacing={1}>
          <Skeleton variant="rectangular" height={40} />
        </Stack>
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="info" sx={{ mb: 3 }}>
        Unable to load saved payment methods. You can still add a new card below.
      </Alert>
    );
  }

  if (!paymentMethods || paymentMethods.length === 0) {
    return null; // Don't show anything if no saved cards
  }

  const formatCardBrand = (brand: string) => {
    return brand.charAt(0).toUpperCase() + brand.slice(1);
  };

  const formatExpiry = (month: number, year: number) => {
    return `${month.toString().padStart(2, "0")}/${year.toString().slice(-2)}`;
  };

  return (
    <Box sx={{ mb: 3 }}>
      <Typography variant="body1" sx={{ mb: 4, fontSize: "1.25rem", fontWeight: 400 }}>
        Saved Payment Methods
      </Typography>
      <FormControl component="fieldset" fullWidth>
        <RadioGroup
          value={selectedPaymentMethod || ""}
          // sx={{ width: "100%" }}
          onChange={(e) => onSelectionChange(e.target.value || null)}
        >
          {paymentMethods?.map((method) => (
            <FormControlLabel
              key={`${method.id}-${defaultPaymentMethodId || "none"}`}
              value={method.id}
              control={<Radio />}
              slotProps={{ typography: { sx: { width: "100%" } } }}
              label={
                <Paper
                  elevation={0}
                  sx={{
                    p: 2,
                    border: "1px solid",
                    borderColor: "divider",
                    borderRadius: 1,
                    width: "100%",
                  }}
                >
                  <Stack direction="row" alignItems="center" spacing={2}>
                    {getBrandImageSrc(method.card.brand) ? (
                      <Box
                        component="img"
                        src={getBrandImageSrc(method.card.brand) as string}
                        alt={`${method.card.brand} logo`}
                        sx={{ height: 24, width: "auto" }}
                      />
                    ) : (
                      <Box sx={{ height: 24, width: 36 }} />
                    )}
                    <Typography variant="body1" fontWeight={500}>
                      {formatCardBrand(method.card.brand)} •••• {method.card.last4}
                    </Typography>
                    <Box sx={{ flex: 1 }} />
                    <Stack direction="row" alignItems="center" spacing={2.5}>
                      {defaultPaymentMethodId === method.id ? (
                        <Box
                          sx={{
                            backgroundColor: "primary.main",
                            color: "primary.contrastText",
                            borderRadius: 999,
                            px: 1.25,
                            py: 0.25,
                            fontSize: "0.7rem",
                            fontWeight: 700,
                            lineHeight: 1.4,
                            whiteSpace: "nowrap",
                          }}
                        >
                          Default
                        </Box>
                      ) : null}
                      <Typography variant="body2" color="text.secondary">
                        Expires {formatExpiry(method.card.exp_month, method.card.exp_year)}
                      </Typography>
                    </Stack>
                  </Stack>
                </Paper>
              }
              sx={{ alignItems: "center", mr: 0, mb: 1 }}
            />
          ))}
          {includeNewCardRadio && (
            <FormControlLabel
              key="new_card"
              value="new_card"
              control={<Radio />}
              slotProps={{ typography: { sx: { width: "100%" } } }}
              label={
                <Paper
                  elevation={0}
                  sx={{
                    p: 2,
                    border: "1px dashed",
                    borderColor: "primary.main",
                    borderRadius: 1,
                    width: "100%",
                    backgroundColor: "transparent",
                  }}
                >
                  <Typography variant="body1" fontWeight={500} color="primary">
                    + Add a new card
                  </Typography>
                </Paper>
              }
              sx={{ alignItems: "center", mr: 0, mb: 1 }}
            />
          )}
        </RadioGroup>
      </FormControl>
      {!hideAddNewButton && !includeNewCardRadio && (
        <Button
          variant="outlined"
          fullWidth
          onClick={() => onSelectionChange("new_card")}
          sx={{
            mt: 2,
            py: 2,
            border: "1px dashed",
            borderColor: "primary.main",
            backgroundColor: "transparent",
            width: "100%",
            textAlign: "left",
            justifyContent: "flex-start",
            "&:hover": {
              border: "2px solid",
            },
          }}
        >
          <Typography variant="body1" fontWeight={500} color="primary" sx={{ margin: "auto" }}>
            + Add a new card
          </Typography>
        </Button>
      )}
    </Box>
  );
}
